import React, { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Icon } from '../Icon';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import { MarketingData } from '../../Registration/types';
import './MarketingPreferencesButtons.scss';

export type MarketingPreferencesButtonsProps = {
  name: string;
};

type CategoryKey = 'spins' | 'bingo';
type ChannelKey = 'email' | 'sms' | 'telephone';

type MarketingFieldConfig = {
  [K in CategoryKey]: {
    [C in ChannelKey]: keyof MarketingData;
  };
};

const MARKETING_FIELDS: MarketingFieldConfig = {
  spins: {
    email: 'optInEmailCasino',
    sms: 'optInSmsCasino',
    telephone: 'optInTelephoneCasino',
  },
  bingo: {
    email: 'optInEmailBingo',
    sms: 'optInSmsBingo',
    telephone: 'optInTelephoneBingo',
  },
};

const ALL_MARKETING_FIELDS: (keyof MarketingData)[] = [
  'optInEmailCasino',
  'optInSmsCasino',
  'optInTelephoneCasino',
  'optInEmailBingo',
  'optInSmsBingo',
  'optInTelephoneBingo',
];

const CATEGORY_CONFIG: {
  key: CategoryKey;
  icon: string;
  headingKey: string;
  descriptionKey: string;
  noteKey: string | null;
}[] = [
  {
    key: 'bingo',
    icon: 'BingoBall',
    headingKey: 'my-account.home.settings.communication.bingo.heading',
    descriptionKey: 'my-account.home.settings.communication.bingo.description',
    noteKey: null,
  },
  {
    key: 'spins',
    icon: 'GiftSpins',
    headingKey: 'my-account.home.settings.communication.spins.heading',
    descriptionKey: 'my-account.home.settings.communication.spins.description',
    noteKey: 'my-account.home.settings.communication.spins.note',
  },
];

/**
 * Marketing preferences button selector component.
 *
 * Provides a button-based selection system for marketing preferences with:
 * - Select All control
 * - Two categories: Spins and Bingo
 * - Three channels per category: Email, SMS, Phone
 */
export const MarketingPreferencesButtons: React.FC<MarketingPreferencesButtonsProps> = () => {
  const { setValue, getValues, watch } = useFormContext();
  const { t } = useTranslations();

  // Watch all marketing fields to track changes
  const allValues = watch(ALL_MARKETING_FIELDS);

  const isAllSelected = useMemo(() => {
    const values = ALL_MARKETING_FIELDS.map((field) => getValues(field));

    return values.every(Boolean);
  }, [allValues, getValues]);

  const handleSelectAll = useCallback((): void => {
    const newValue = !isAllSelected;

    ALL_MARKETING_FIELDS.forEach((fieldName) => {
      setValue(fieldName, newValue, { shouldDirty: true });
    });
  }, [isAllSelected, setValue]);

  const handleChannelToggle = useCallback(
    (fieldName: keyof MarketingData): void => {
      const currentValue = getValues(fieldName);

      setValue(fieldName, !currentValue, { shouldDirty: true });
    },
    [getValues, setValue]
  );

  const renderChannelButton = (category: CategoryKey, channel: ChannelKey, iconVariant: string): JSX.Element => {
    const fieldName = MARKETING_FIELDS[category][channel];
    const isSelected = getValues(fieldName);

    const channelLabels: Record<ChannelKey, string> = {
      email: t('my-account.home.settings.communication.email'),
      sms: t('my-account.home.settings.communication.sms'),
      telephone: t('my-account.home.settings.communication.phone'),
    };

    return (
      <button
        key={`${category}-${channel}`}
        type="button"
        className={`marketing-preferences-button marketing-preferences-button--${channel} ${
          isSelected ? 'marketing-preferences-button--selected' : ''
        }`}
        onClick={(): void => handleChannelToggle(fieldName)}
        aria-pressed={isSelected}
      >
        <Icon variant={iconVariant as never} className="marketing-preferences-button__icon" />
        <span className="marketing-preferences-button__label">{channelLabels[channel]}</span>
      </button>
    );
  };

  const renderCategoryRow = (config: (typeof CATEGORY_CONFIG)[0]): JSX.Element => (
    <div key={config.key} className={`marketing-preferences-row marketing-preferences-row--${config.key}`}>
      <div className="marketing-preferences-row__info">
        <div className="marketing-preferences-row__icon-title">
          <div className="marketing-preferences-row__icon">
            <Icon variant={config.icon as never} />
          </div>
          <h4 className="marketing-preferences-row__title">{t(config.headingKey)}</h4>
        </div>
        <div className="marketing-preferences-row__description">
          <p className="marketing-preferences-row__description-text">{t(config.descriptionKey)}</p>
          {config.noteKey && <p className="marketing-preferences-row__description-note">{t(config.noteKey)}</p>}
        </div>
      </div>
      <div className="marketing-preferences-row__buttons">
        {renderChannelButton(config.key, 'email', 'ItvEnvelope')}
        {renderChannelButton(config.key, 'sms', 'Sms')}
        {renderChannelButton(config.key, 'telephone', 'Phone')}
      </div>
    </div>
  );

  const renderCategoryColumn = (config: (typeof CATEGORY_CONFIG)[0]): JSX.Element => (
    <div key={config.key} className={`marketing-preferences-col marketing-preferences-col--${config.key}`}>
      <div className="marketing-preferences-col__header">
        <div className="marketing-preferences-col__icon-title">
          <div className="marketing-preferences-col__icon">
            <Icon variant={config.icon as never} />
          </div>
          <h4 className="marketing-preferences-col__title">{t(config.headingKey)}</h4>
        </div>
      </div>
      <div className="marketing-preferences-col__description">
        <p className="marketing-preferences-col__description-text">{t(config.descriptionKey)}</p>
        {config.noteKey && <p className="marketing-preferences-col__description-note">{t(config.noteKey)}</p>}
      </div>
      <div className="marketing-preferences-col__buttons">
        {renderChannelButton(config.key, 'email', 'ItvEnvelope')}
        {renderChannelButton(config.key, 'sms', 'Sms')}
        {renderChannelButton(config.key, 'telephone', 'Phone')}
      </div>
    </div>
  );

  return (
    <div className="marketing-preferences-buttons">
      {/* Select All Button */}
      <button
        type="button"
        className={`marketing-preferences-select-all ${
          isAllSelected ? 'marketing-preferences-select-all--checked' : ''
        }`}
        onClick={handleSelectAll}
        aria-pressed={isAllSelected}
      >
        <span className="marketing-preferences-select-all__checkbox">
          {isAllSelected && <Icon variant="Check" className="marketing-preferences-select-all__check-icon" />}
        </span>
        <span className="marketing-preferences-select-all__label">
          {t('my-account.home.settings.communication.all')}
        </span>
      </button>

      {/* Subheading */}
      <h5 className="marketing-preferences-subheading">{t('my-account.home.settings.communication.subheading')}</h5>

      {/* Desktop: Row layout */}
      <div className="marketing-preferences-desktop">{CATEGORY_CONFIG.map(renderCategoryRow)}</div>

      {/* Mobile: Column layout (reversed - Spins left, Bingo right) */}
      <div className="marketing-preferences-mobile">{CATEGORY_CONFIG.slice().reverse().map(renderCategoryColumn)}</div>
    </div>
  );
};
